"use client";

import type React from "react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "plyr/dist/plyr.css";
import "@/styles/plyr.css";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

// Import Plyr dynamically to avoid SSR issues
let Plyr: typeof import("plyr").default | null = null;
if (typeof window !== "undefined") {
  import("plyr").then((module) => {
    Plyr = module.default;
  });
}

export type VideoProvider = "vimeo" | "youtube" | "direct";

export interface VideoChapter {
  title: string;
  time: number;
}

export interface VideoTranscriptEntry {
  time: number;
  text: string;
}

export interface PlyrVideoPlayerProps {
  videoUrl: string;
  provider?: VideoProvider;
  title?: string;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  onWatchTimeUpdate?: (watchedSeconds: number) => void;
  onMaxWatchedSecondsUpdate?: (maxWatchedSeconds: number) => void;
  watchTimeInterval?: number;
  autoPlay?: boolean;
  className?: string;
  chapters?: VideoChapter[];
  transcript?: VideoTranscriptEntry[];
  completionThreshold?: number;
  onCompletionAllowedChange?: (canComplete: boolean) => void;
  preventForwardSeek?: boolean;
  initialMaxWatchedSeconds?: number;
}

const COMPLETION_THRESHOLD_DEFAULT = 0.75;

function useLatestRef<T>(value: T) {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  return valueRef;
}

function detectVideoProvider(url: string): VideoProvider {
  if (url.includes("vimeo.com")) return "vimeo";
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  return "direct";
}

function extractVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function formatTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }

  const rounded = Math.floor(totalSeconds);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function PlyrVideoPlayer({
  videoUrl,
  provider,
  title,
  onProgress,
  onComplete,
  onWatchTimeUpdate,
  onMaxWatchedSecondsUpdate,
  watchTimeInterval = 5,
  autoPlay = false,
  className = "",
  chapters,
  transcript,
  completionThreshold = COMPLETION_THRESHOLD_DEFAULT,
  onCompletionAllowedChange,
  preventForwardSeek = false,
  initialMaxWatchedSeconds = 0,
}: PlyrVideoPlayerProps) {
  const detectedProvider = provider || detectVideoProvider(videoUrl);
  const playerRef = useRef<Plyr | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const watchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const watchedSecondsRef = useRef(0);
  const maxWatchedSecondsRef = useRef(0);
  const lastValidTimeRef = useRef(0);
  const ignoreSeekRef = useRef(false);
  const completionAllowedRef = useRef(false);
  const onCompletionAllowedChangeRef = useLatestRef(onCompletionAllowedChange);
  const onProgressRef = useLatestRef(onProgress);
  const completionThresholdValue = useMemo(() => {
    if (!Number.isFinite(completionThreshold)) {
      return COMPLETION_THRESHOLD_DEFAULT;
    }
    return Math.min(Math.max(completionThreshold, 0), 1);
  }, [completionThreshold]);
  const effectiveWatchInterval = Math.max(1, watchTimeInterval);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const durationRef = useLatestRef(duration);

  const activeTranscriptIndex = useMemo(() => {
    if (!transcript?.length) return -1;
    for (let i = transcript.length - 1; i >= 0; i -= 1) {
      if (currentTime >= transcript[i].time) {
        return i;
      }
    }
    return -1;
  }, [transcript, currentTime]);

  const activeChapterIndex = useMemo(() => {
    if (!chapters?.length) return -1;
    for (let i = chapters.length - 1; i >= 0; i -= 1) {
      if (currentTime >= chapters[i].time) {
        return i;
      }
    }
    return -1;
  }, [chapters, currentTime]);

  const onMaxWatchedSecondsUpdateRef = useLatestRef(onMaxWatchedSecondsUpdate);

  // Initialize player
  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined") return;

    // Wait for Plyr to be loaded
    if (!Plyr) {
      const checkPlyr = setInterval(() => {
        if (Plyr) {
          clearInterval(checkPlyr);
        }
      }, 100);
      return () => clearInterval(checkPlyr);
    }

    watchedSecondsRef.current = 0;
    maxWatchedSecondsRef.current = initialMaxWatchedSeconds;
    completionAllowedRef.current = false;
    lastValidTimeRef.current = 0;
    ignoreSeekRef.current = false;
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(autoPlay);
    onCompletionAllowedChangeRef.current?.(false);

    let player: import("plyr").default;
    const container = containerRef.current;

    // Build the source based on provider
    if (detectedProvider === "youtube") {
      const videoId = extractYouTubeId(videoUrl);
      if (!videoId) return;

      // Add hash parameter to hide title on YouTube embeds
      container.innerHTML = `<div data-plyr-provider="youtube" data-plyr-embed-id="${videoId}"></div>`;
      player = new Plyr(container.querySelector('[data-plyr-provider]')!, {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'],
        settings: ['quality', 'speed'],
        hideControls: false,
        youtube: {
          noCookie: true,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          cc_load_policy: 0,
          fs: 0,
        },
      });
    } else if (detectedProvider === "vimeo") {
      const videoId = extractVimeoId(videoUrl);
      if (!videoId) return;

      container.innerHTML = `<div data-plyr-provider="vimeo" data-plyr-embed-id="${videoId}"></div>`;
      player = new Plyr(container.querySelector('[data-plyr-provider]')!, {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'pip', 'fullscreen'],
        settings: ['quality', 'speed'],
        hideControls: false,
        vimeo: {
          byline: false,
          portrait: false,
          title: false,
          speed: true,
          transparent: false,
        },
      });
    } else {
      container.innerHTML = `<video src="${videoUrl}" playsinline></video>`;
      player = new Plyr(container.querySelector('video')!, {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'pip', 'fullscreen'],
        settings: ['speed'],
      });
    }

    playerRef.current = player;

    // Event handlers
    const handleTimeUpdate = () => {
      if (!player) return;

      const time = player.currentTime;
      const total = player.duration;

      if (total > 0) {
        setDuration(total);
      }

      const effectiveDuration = total > 0 ? total : durationRef.current;
      const clampedTime = effectiveDuration > 0 ? Math.min(time, effectiveDuration) : Math.max(time, 0);
      const previousMax = maxWatchedSecondsRef.current;

      // Ignore backwards time jumps to 0 during playback
      if (clampedTime === 0 && lastValidTimeRef.current > 0) {
        return;
      }

      lastValidTimeRef.current = clampedTime;

      if (effectiveDuration > 0 && clampedTime > previousMax) {
        maxWatchedSecondsRef.current = clampedTime;
        const progressValue = clampedTime / effectiveDuration;
        onProgressRef.current?.(progressValue);
        onMaxWatchedSecondsUpdateRef.current?.(clampedTime);

        if (!completionAllowedRef.current && progressValue >= completionThresholdValue) {
          completionAllowedRef.current = true;
          onCompletionAllowedChangeRef.current?.(true);
        }
      } else if (effectiveDuration <= 0 && clampedTime > previousMax) {
        maxWatchedSecondsRef.current = clampedTime;
        onMaxWatchedSecondsUpdateRef.current?.(clampedTime);
        if (previousMax === 0 && !completionAllowedRef.current) {
          onProgressRef.current?.(0);
        }
      }

      setCurrentTime(clampedTime);
    };

    const handleSeeking = () => {
      if (!player || !preventForwardSeek) return;

      if (ignoreSeekRef.current) {
        ignoreSeekRef.current = false;
        return;
      }

      const seekTime = player.currentTime;
      const maxAllowed = maxWatchedSecondsRef.current;
      const seekBuffer = 2;

      if (seekTime > maxAllowed + seekBuffer) {
        ignoreSeekRef.current = true;
        player.currentTime = maxAllowed;
      }
    };

    const handleSeeked = () => {
      if (!player || !preventForwardSeek) return;

      if (ignoreSeekRef.current) {
        ignoreSeekRef.current = false;
        return;
      }

      const seekTime = player.currentTime;
      const maxAllowed = maxWatchedSecondsRef.current;
      const seekBuffer = 2;

      if (seekTime > maxAllowed + seekBuffer) {
        ignoreSeekRef.current = true;
        player.currentTime = maxAllowed;
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onComplete?.();
    };

    const handleReady = () => {
      if (player.duration) {
        setDuration(player.duration);
      }
      if (autoPlay) {
        player.play().catch(() => {
          // Autoplay may be blocked
        });
      }
    };

    // Attach event listeners
    player.on('timeupdate', handleTimeUpdate);
    player.on('seeking', handleSeeking);
    player.on('seeked', handleSeeked);
    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('ended', handleEnded);
    player.on('ready', handleReady);

    return () => {
      if (watchIntervalRef.current) {
        clearInterval(watchIntervalRef.current);
        watchIntervalRef.current = null;
      }
      player.destroy();
      playerRef.current = null;
    };
  }, [
    videoUrl,
    detectedProvider,
    autoPlay,
    initialMaxWatchedSeconds,
    preventForwardSeek,
    completionThresholdValue,
    onComplete,
    durationRef,
    onProgressRef,
    onMaxWatchedSecondsUpdateRef,
    onCompletionAllowedChangeRef,
  ]);

  // Watch time interval tracking
  useEffect(() => {
    if (!onWatchTimeUpdate) {
      return;
    }

    if (watchIntervalRef.current) {
      clearInterval(watchIntervalRef.current);
      watchIntervalRef.current = null;
    }

    if (!isPlaying) {
      return;
    }

    watchIntervalRef.current = setInterval(() => {
      watchedSecondsRef.current += effectiveWatchInterval;
      onWatchTimeUpdate(watchedSecondsRef.current);
    }, effectiveWatchInterval * 1000);

    return () => {
      if (watchIntervalRef.current) {
        clearInterval(watchIntervalRef.current);
        watchIntervalRef.current = null;
      }
    };
  }, [isPlaying, onWatchTimeUpdate, effectiveWatchInterval]);

  const handleChapterClick = useCallback(
    (time: number) => {
      if (!playerRef.current) return;

      // Prevent seeking forward beyond max watched
      if (preventForwardSeek) {
        const maxAllowed = maxWatchedSecondsRef.current;
        const seekBuffer = 2;
        if (time > maxAllowed + seekBuffer) {
          return;
        }
      }

      playerRef.current.currentTime = time;
    },
    [preventForwardSeek]
  );

  const handleTranscriptClick = useCallback(
    (time: number) => {
      if (!playerRef.current) return;

      // Prevent seeking forward beyond max watched
      if (preventForwardSeek) {
        const maxAllowed = maxWatchedSecondsRef.current;
        const seekBuffer = 2;
        if (time > maxAllowed + seekBuffer) {
          return;
        }
      }

      playerRef.current.currentTime = time;
    },
    [preventForwardSeek]
  );

  if (!videoUrl) {
    return (
      <div className={`aspect-video bg-muted rounded-lg flex items-center justify-center ${className}`}>
        <p className="text-muted-foreground">No video available</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-6 lg:flex-row ${className}`}>
      <div className="flex-1 space-y-4">
        <div
          ref={containerRef}
          className="relative aspect-video overflow-hidden rounded-lg bg-black plyr-container"
          title={title}
          data-prevent-seek={preventForwardSeek ? "true" : "false"}
        />

        {/* Custom progress info - shows max watched if preventForwardSeek is enabled */}
        {preventForwardSeek && duration > 0 && (
          <div className="text-xs text-muted-foreground">
            Progress: {formatTime(currentTime)} / {formatTime(duration)}
            {maxWatchedSecondsRef.current > currentTime && (
              <span className="ml-2">
                (Max watched: {formatTime(maxWatchedSecondsRef.current)})
              </span>
            )}
          </div>
        )}

        {chapters?.length ? (
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <h3 className="mb-2 text-sm font-semibold text-foreground">Chapters</h3>
            <div className="flex flex-wrap gap-2">
              {chapters.map((chapter, index) => {
                const isActive = index === activeChapterIndex;
                const isLocked = preventForwardSeek && chapter.time > maxWatchedSecondsRef.current + 2;
                return (
                  <Button
                    key={`${chapter.title}-${chapter.time}`}
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => handleChapterClick(chapter.time)}
                    disabled={isLocked}
                  >
                    {chapter.title}
                    {isLocked && " 🔒"}
                  </Button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {transcript?.length ? (
        <div className="lg:w-80">
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Transcript</h3>
            <ScrollArea className="h-80">
              <ul className="space-y-2 pr-2">
                {transcript.map((entry, index) => {
                  const isActive = index === activeTranscriptIndex;
                  const isLocked = preventForwardSeek && entry.time > maxWatchedSecondsRef.current + 2;
                  return (
                    <li key={`${entry.time}-${entry.text}`}>
                      <button
                        type="button"
                        onClick={() => handleTranscriptClick(entry.time)}
                        disabled={isLocked}
                        className={`flex w-full items-start gap-3 rounded-md p-2 text-left text-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : isLocked
                            ? "text-muted-foreground/50 cursor-not-allowed"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        <span className="mt-0.5 w-14 flex-shrink-0 text-xs font-medium uppercase tracking-wide">
                          {formatTime(entry.time)}
                          {isLocked && " 🔒"}
                        </span>
                        <span>{entry.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          </div>
        </div>
      ) : null}
    </div>
  );
}
